import { z } from 'zod';

/**
 * Accounts: sign-in with a mobile number (Firebase phone auth), a small profile, and consents.
 *
 *   POST  /api/v1/auth/session   (Firebase ID token)  -> SessionResponse   create the account on first sign-in
 *   POST  /api/v1/auth/logout                         -> 204               sign out on every device
 *   GET   /api/v1/me                                  -> MeResponse
 *   PATCH /api/v1/me             UpdateMe             -> MeResponse
 *   GET   /api/v1/me/consents                         -> ConsentList
 *   POST  /api/v1/me/consents    RecordConsent        -> ConsentResponse
 */

/**
 * Firebase project ids for the Auth emulator ("demo-" ids never reach real Firebase). Tests use their own,
 * so a test emulator never mixes with the one `npm run dev` runs (they would share one hub otherwise).
 */
export const FIREBASE_EMULATOR_PROJECT_ID = 'demo-oneapp';
export const FIREBASE_TEST_PROJECT_ID = 'demo-oneapp-test';

export const CHAT_LANGUAGES = ['en', 'hi'] as const;
export const ChatLanguageSchema = z.enum(CHAT_LANGUAGES);
export type ChatLanguage = z.infer<typeof ChatLanguageSchema>;

/**
 * Every consent text people can accept, oldest version first. Changing the text means adding a
 * version here: everyone is then asked to accept the new one.
 *
 *   privacy_notice  the privacy notice (including AI chat, 2026-10-11)
 *   adult           "I'm 18 or older" (Phase 17)
 *   ai_photos       invoice photos read by AI, OpenAI in the USA (Phase 17): asked once, withdrawable
 */
export const CONSENT_VERSIONS = {
  privacy_notice: ['2026-09-29', '2026-10-04', '2026-10-11'],
  adult: ['2026-10-04'],
  ai_photos: ['2026-10-04'],
} as const satisfies Record<string, readonly [string, ...string[]]>;

export const CONSENT_TYPES = Object.keys(CONSENT_VERSIONS) as [ConsentType, ...ConsentType[]];
export type ConsentType = keyof typeof CONSENT_VERSIONS;
export const ConsentTypeSchema = z.enum(['privacy_notice', 'adult', 'ai_photos']);

export function currentConsentVersion(type: ConsentType): string {
  const versions: readonly string[] = CONSENT_VERSIONS[type];
  return versions[versions.length - 1] as string;
}

export function isKnownConsentVersion(type: ConsentType, version: string): boolean {
  return (CONSENT_VERSIONS[type] as readonly string[]).includes(version);
}

/**
 * What someone still has to do before they can use EkApp: a name (profile); agree to the privacy notice
 * the first time (consent) or again when it changed (notice); say they're 18 or older (adult); and, when
 * their account is set to be deleted, keep it first (keep).
 */
export const NeedSchema = z.enum(['profile', 'consent', 'notice', 'adult', 'keep']);
export type Need = z.infer<typeof NeedSchema>;

/** The account is set to be deleted (Phase 17): when it was asked for, and when it goes. */
export const DeletionSchema = z.object({ requestedAt: z.string(), dueAt: z.string() });
export type Deletion = z.infer<typeof DeletionSchema>;

export const MeSchema = z.object({
  id: z.string(),
  /** Always masked, e.g. "+91 98450 ••321". The full number never leaves the server. */
  phoneMasked: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  chatLanguage: ChatLanguageSchema,
  createdAt: z.string(),
});
export type Me = z.infer<typeof MeSchema>;

export const SessionResponseSchema = z.object({
  user: MeSchema,
  /** True only for the request that created the account. */
  isNew: z.boolean(),
  needs: z.array(NeedSchema),
  /** Set while the account waits to be deleted (Phase 17). */
  deletion: DeletionSchema.nullable().default(null),
  /** Their admin roles (gap 2). */
  roles: z.array(z.enum(['admin', 'grievance'])).default([]),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

export const MeResponseSchema = z.object({
  user: MeSchema,
  needs: z.array(NeedSchema),
  /** Set while the account waits to be deleted (Phase 17). */
  deletion: DeletionSchema.nullable().default(null),
  /** Their admin roles (gap 2): Menu → Admin shows when there's one. */
  roles: z.array(z.enum(['admin', 'grievance'])).default([]),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

// Letters in any script, marks, spaces and . ' - (names like "Dr. A. K. D’Souza"); no digits or symbols.
const NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M} .'’-]*$/u;

export const PersonNameSchema = z
  .string()
  .transform((value) => value.normalize('NFC').replace(/\s+/g, ' ').trim())
  .pipe(
    z
      .string()
      .min(1, { error: 'Enter your name' })
      .max(80, { error: 'Keep it under 80 characters' })
      .regex(NAME_PATTERN, { error: 'Use letters only' }),
  );

export const EmailSchema = z
  .string()
  .trim()
  .max(254, { error: 'That email is too long' })
  .pipe(z.email({ error: 'Enter an email like name@example.com' }));

export const UpdateMeSchema = z
  .strictObject({
    name: PersonNameSchema.optional(),
    /** null removes it. */
    email: EmailSchema.nullable().optional(),
    chatLanguage: ChatLanguageSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { error: 'Send at least one field to change' });
export type UpdateMe = z.input<typeof UpdateMeSchema>;

export const ConsentSchema = z.object({
  type: ConsentTypeSchema,
  version: z.string(),
  acceptedAt: z.string(),
  /** Withdrawn (only what can be on its own: invoice photos read by AI). */
  withdrawnAt: z.string().nullable().default(null),
});
export type Consent = z.infer<typeof ConsentSchema>;

/**
 * Permissions to use someone's data for a purpose, given per business (the DPDP Act's consent with a
 * purpose, a scope and an end). Each text is versioned like the privacy notice; the web and chat show
 * the current version's words.
 */
export const DATA_CONSENT_VERSIONS = {
  gst_data: ['2026-09-29'],
  /** Reading a bank or UPI statement for a month's return (Phase 16; in the ledger from Phase 17). */
  bank_statement: ['2026-10-04'],
} as const satisfies Record<string, readonly [string, ...string[]]>;
export type DataConsentPurpose = keyof typeof DATA_CONSENT_VERSIONS;

export function currentDataConsentVersion(purpose: DataConsentPurpose): string {
  const versions: readonly string[] = DATA_CONSENT_VERSIONS[purpose];
  return versions[versions.length - 1] as string;
}

/** What connecting a GST account allows: reading returns, ledgers and GSTR-2B; filing only with an OTP. */
export const GST_DATA_SCOPES = [
  'read_returns',
  'read_ledgers',
  'read_gstr2b',
  'prepare_returns',
  'file_with_otp',
] as const;

/** What reading a statement allows: sorting the money that came in, for that month's return. */
export const STATEMENT_SCOPES = ['read_statement', 'keep_payments_until_filed'] as const;

export const DataConsentSchema = z.object({
  id: z.string(),
  purpose: z.enum(['gst_data', 'bank_statement']),
  version: z.string(),
  /** The business it's for. `id` is null once the business is no longer in anyone's list. */
  business: z.object({ id: z.string().nullable(), name: z.string(), gstin: z.string() }),
  scope: z.array(z.enum([...GST_DATA_SCOPES, ...STATEMENT_SCOPES])),
  /** A statement's month, YYYY-MM (bank_statement only). */
  period: z.string().nullable().default(null),
  givenAt: z.string(),
  /** When it ends on its own (the portal's API access period), if known. */
  endsAt: z.string().nullable(),
  /** When it was withdrawn (by disconnecting, or removing the business). */
  withdrawnAt: z.string().nullable(),
});
export type DataConsent = z.infer<typeof DataConsentSchema>;

export const ConsentListSchema = z.object({
  consents: z.array(ConsentSchema),
  dataConsents: z.array(DataConsentSchema),
});
export type ConsentList = z.infer<typeof ConsentListSchema>;
export const ConsentResponseSchema = z.object({ consent: ConsentSchema });

export const RecordConsentSchema = z.strictObject({
  type: ConsentTypeSchema,
  version: z.string().min(1).max(40),
});
export type RecordConsent = z.infer<typeof RecordConsentSchema>;

// ---------------------------------------------------------------------------
// Indian mobile numbers
// ---------------------------------------------------------------------------

export type MobileProblem = 'empty' | 'length' | 'start';

/**
 * Reads a mobile number however people type or paste it — "98450 12321", "+91 98450-12321",
 * "09845012321" — and returns its 10 digits, or why it isn't a valid Indian mobile number.
 */
export function parseIndianMobile(raw: string): { ok: true; digits: string } | { ok: false; problem: MobileProblem } {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return { ok: false, problem: 'empty' };
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length !== 10) return { ok: false, problem: 'length' };
  if (!/^[6-9]/.test(digits)) return { ok: false, problem: 'start' };
  return { ok: true, digits };
}

/** "9845012321" → "98450 12321" (how Indian mobile numbers are usually written). */
export function formatIndianMobile(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  return d.length > 5 ? `${d.slice(0, 5)} ${d.slice(5)}` : d;
}

/**
 * "+919845012321" → "+91 ••••• ••321": only the last three digits, for API responses and menus.
 * With `showStart` → "+91 98450 ••321", for the person who has just typed it (so a typo shows).
 */
export function maskPhone(e164: string, { showStart = false } = {}): string {
  const match = /^\+91([6-9]\d{4})\d{2}(\d{3})$/.exec(e164);
  if (match) return `+91 ${showStart ? match[1] : '•••••'} ••${match[2]}`;
  const tail = e164.replace(/\D/g, '').slice(-3);
  return tail ? `••${tail}` : '';
}
